import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { isEmpty } from 'ramda'
import { useSelector } from 'hooks/redux'
import strings from 'utils/strings'
import { TextInput } from 'components/patterns/Inputs'
import AlertIcon from 'img/alert.svg'

import {
  getDomainsList,
  selectPtoVehicleFragments,
} from 'selectors/powerTakeOff'

import { Row, Col } from 'components/patterns'
import TemplateDiffViewModal from '../../TemplateDiffViewModal'

import {
  StyledTable,
  StyledCheckBox,
  PtoDomainElement,
  PtoDomainElementTitle,
  PtoDomainPopDetails,
  ChooseDomainTitle,
  TemplateDetailsEdit,
  InputRow,
  StyledTextArea,
  PtoRightSideItemsWrapper,
  Divider,
  PtoOutofRangeWarningContainer,
  WarningIcon,
  OutofRangeWarningText,
  DisabledText,
} from './PtoTemplateDiffTable.style'

const columns = [
  {
    title: strings.setting_name,
    dataIndex: 'name',
    key: 'name',
    textWrap: 'word-break',
    width: '47.82%',
  },
  {
    title: strings.pto_value_on_vehicle,
    dataIndex: 'vehicleValue',
    key: 'vehicleValue',
    width: '17.39%',
  },
  {
    title: strings.refNo,
    dataIndex: 'refNo',
    key: 'refNo',
    width: '8.69%',
  },
  {
    title: strings.pto_conflict_setting,
    dataIndex: 'templateValue',
    key: 'templateValue',
    textWrap: 'word-break',
    width: '26.08%',
    render: (
      _,
      { isFragmentOutOfRange, vehType, vehicleUnits, rangeValues }
    ) => {
      if (!isFragmentOutOfRange) {
        return <DisabledText>{strings.none}</DisabledText>
      }
      if (vehType === 'range') {
        return strings.pto_fragment_recommended_range(
          Math.min(...rangeValues),
          Math.max(...rangeValues),
          vehicleUnits
        )
      }
      if (vehType === 'state') {
        return strings.pto_fragment_out_of_range_no_valid
      }
      if (vehType === 'boolean') {
        return strings.pto_fragment_recommended_bool_range(!rangeValues[0])
      }
      return ''
    },
  },
]

const PtoTemplateDiffTable = ({
  setIsGlobalEnabled,
  templateName,
  setTemplateName,
  templateDescription,
  setTemplateDescription,
  selectedDomains,
  setSelectedDomains,
}) => {
  const ptoVehicleFragmentsList = useSelector(selectPtoVehicleFragments)
  const domainsList = useSelector(getDomainsList)

  const DomainCheckbox = ({ checkBoxDomainId }) => {
    const [isChecked, setIsChecked] = useState(
      selectedDomains[checkBoxDomainId]
    )

    const toggleChecked = () => {
      selectedDomains[checkBoxDomainId] = !selectedDomains[checkBoxDomainId]
      setIsChecked(selectedDomains[checkBoxDomainId])
      setSelectedDomains(selectedDomains)
    }

    const handleClick = () => {
      toggleChecked()
    }

    const handleChange = () => {
      toggleChecked()
    }

    DomainCheckbox.propTypes = {
      checkBoxDomainId: PropTypes.string,
    }

    return (
      <StyledCheckBox
        checked={isChecked}
        onClick={handleClick}
        onChange={handleChange}
        className="pto-diff-row"
      />
    )
  }

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [domainTitle, setDomainTitle] = useState('')
  const [isFormEnabled, setIsFormEnabled] = useState(false)
  const currentFormState = !isEmpty(templateName) && templateName.length <= 80
  useEffect(() => {
    setIsFormEnabled(currentFormState)
  }, [currentFormState])

  const handleCloseModal = () => {
    setModalIsOpen(false)
  }

  const templateDiffViewModalButtons = [
    {
      type: 'secondary',
      text: 'Close',
      onClick: () => {
        handleCloseModal()
      },
    },
  ]

  const diffListTable = useMemo(
    () => (
      <StyledTable
        size="middle"
        columns={columns}
        dataSource={ptoVehicleFragmentsList.filter(
          (item) => item.domainId === domainTitle
        )}
        pagination={false}
        yScroll={350}
        rowKey={(record) => record.refNo}
      />
    ),
    [ptoVehicleFragmentsList, domainTitle]
  )

  const handleTemplateNameChange = (value) => {
    const formState = !isEmpty(value) && value.length <= 80
    setTemplateName(value)
    setIsFormEnabled(formState)
    setIsGlobalEnabled(formState)
  }

  const handleTemplateDescriptionChange = (value) => {
    setTemplateDescription(value)
  }

  return (
    <>
      <InputRow>
        <Col span={5}>
          <TemplateDetailsEdit>
            <TextInput
              label={strings.pto_template_name_title}
              placeholder={strings.pto_template_name_placeholder}
              value={templateName}
              onChange={handleTemplateNameChange}
              name="template-name"
              rules={yup.string().required().max(80)}
            />
          </TemplateDetailsEdit>
        </Col>
        <Col span={9}>
          <TemplateDetailsEdit>
            <StyledTextArea
              label={strings.pto_template_name_description}
              size="large"
              onChange={handleTemplateDescriptionChange}
              value={templateDescription}
              placeholder={strings.create_template_add_description}
              maxLength={1000}
              name="template-description"
              rules={yup.string().max(1000)}
              rows={1}
            />
          </TemplateDetailsEdit>
        </Col>
      </InputRow>

      <Row className="diff-results-row">
        <Col span={24} className="diff-results-col">
          <ChooseDomainTitle isFormEnabled={isFormEnabled}>
            {strings.create_template_choose_domains}
          </ChooseDomainTitle>
        </Col>
      </Row>

      <Row className="diff-results-row">
        <Col span={24} className="diff-results-col">
          {domainsList.map((domain) => (
            <PtoDomainElement key={domain.id}>
              <PtoDomainElementTitle>
                <DomainCheckbox checkBoxDomainId={domain.id} />
                &nbsp;&nbsp;
                {domain.name}
              </PtoDomainElementTitle>
              <PtoRightSideItemsWrapper>
                {domain.domainHasOutofRangeSettings && (
                  <>
                    <PtoOutofRangeWarningContainer>
                      <WarningIcon src={AlertIcon} />
                      <OutofRangeWarningText>
                        {strings.pto_template_out_of_range_text}
                      </OutofRangeWarningText>
                    </PtoOutofRangeWarningContainer>
                    <Divider />
                  </>
                )}
                <PtoDomainPopDetails
                  onClick={() => {
                    setDomainTitle(domain.name)
                    setModalIsOpen(true)
                  }}
                  data-testid="view-details-link"
                >
                  {strings.pto_template_view_details}
                </PtoDomainPopDetails>
              </PtoRightSideItemsWrapper>
            </PtoDomainElement>
          ))}

          {modalIsOpen && (
            <TemplateDiffViewModal
              modalIsOpen={modalIsOpen}
              onClose={handleCloseModal}
              templateDiffViewModalButtons={templateDiffViewModalButtons}
              diffListTable={diffListTable}
              domainTitle={domainTitle}
            />
          )}
        </Col>
      </Row>
    </>
  )
}

PtoTemplateDiffTable.propTypes = {
  setIsGlobalEnabled: PropTypes.func,
  templateName: PropTypes.string,
  setTemplateName: PropTypes.func,
  templateDescription: PropTypes.string,
  setTemplateDescription: PropTypes.func,
  selectedDomains: PropTypes.object,
  setSelectedDomains: PropTypes.func,
}

export default PtoTemplateDiffTable
